import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Megaphone, Plus, ThumbsUp, Clock,
  CheckCircle, XCircle, AlertCircle, Loader2, ChevronRight,
  Send, Bug, Lightbulb, Sparkles, HelpCircle, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const feedbackTypeIcons = {
  bug: Bug,
  feature_request: Lightbulb,
  improvement: Sparkles,
  general: HelpCircle
};

const feedbackTypeLabels = {
  bug: 'Bug Report',
  feature_request: 'Feature Request',
  improvement: 'Improvement',
  general: 'General Feedback'
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  planned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800'
};

const releaseTypeColors = {
  new_feature: 'bg-green-100 text-green-800',
  improvement: 'bg-blue-100 text-blue-800',
  bug_fix: 'bg-yellow-100 text-yellow-800',
  security: 'bg-red-100 text-red-800',
  announcement: 'bg-purple-100 text-purple-800'
};

const FeedbackPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('release-notes');
  const [loading, setLoading] = useState(false);
  
  // Feedback state
  const [myFeedback, setMyFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showNewFeedback, setShowNewFeedback] = useState(false);
  const [newFeedback, setNewFeedback] = useState({
    title: '',
    description: '',
    feedback_type: 'general',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Release notes state
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNote, setSelectedNote] = useState(null);

  const isVendor = user?.role === 'vendor';

  useEffect(() => {
    loadReleaseNotes();
    loadUnreadCount();
    if (isVendor) {
      loadMyFeedback();
    }
  }, [isVendor]);

  const loadReleaseNotes = async () => {
    setReleasesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes?published_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReleaseNotes(data.release_notes || []);
      }
    } catch (error) {
      console.error('Failed to load release notes:', error);
    } finally {
      setReleasesLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes/unread/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadMyFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyFeedback(data.feedback || []);
      }
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!newFeedback.title.trim() || !newFeedback.description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newFeedback)
      });

      if (res.ok) {
        toast.success('Feedback submitted successfully!');
        setShowNewFeedback(false);
        setNewFeedback({ title: '', description: '', feedback_type: 'general', category: '' });
        loadMyFeedback();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/${feedbackId}/upvote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.action === 'added' ? 'Upvoted!' : 'Upvote removed');
        loadMyFeedback();
      }
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const viewReleaseNote = async (note) => {
    setSelectedNote(note);
    // This will mark it as viewed via the API
    try {
      await fetch(`${API_URL}/api/feedback/release-notes/${note.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadUnreadCount();
    } catch (error) {
      console.error('Error marking as viewed:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="feedback-page">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Feedback & Updates</h1>
          <p className="text-gray-600 mt-1">
            Share your feedback, request features, and stay updated with platform releases
          </p>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="release-notes" className="flex items-center gap-2" data-testid="release-notes-tab">
            <Megaphone className="h-4 w-4" />
            Release Notes
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          {isVendor && (
            <TabsTrigger value="my-feedback" className="flex items-center gap-2" data-testid="my-feedback-tab">
              <MessageSquare className="h-4 w-4" />
              My Feedback
            </TabsTrigger>
          )}
        </TabsList>

        {/* Release Notes Tab */}
        <TabsContent value="release-notes" className="space-y-4">
          {releasesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : releaseNotes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Megaphone className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Release Notes Yet</h3>
                <p className="text-gray-500 mt-1">Stay tuned for platform updates!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {releaseNotes.map((note) => (
                <Card 
                  key={note.id} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    note.is_pinned ? 'border-blue-300 bg-blue-50/30' : ''
                  }`}
                  onClick={() => viewReleaseNote(note)}
                  data-testid={`release-note-${note.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {note.is_pinned && (
                            <Badge variant="outline" className="text-blue-600 border-blue-300">
                              Pinned
                            </Badge>
                          )}
                          <Badge className={releaseTypeColors[note.note_type] || 'bg-gray-100'}>
                            {note.note_type?.replace('_', ' ')}
                          </Badge>
                          {note.version && (
                            <span className="text-sm text-gray-500">v{note.version}</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{note.title}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {note.content?.substring(0, 150)}...
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDate(note.published_at || note.created_at)}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* My Feedback Tab (Vendors only) */}
        {isVendor && (
          <TabsContent value="my-feedback" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Your Submissions</h2>
              <Dialog open={showNewFeedback} onOpenChange={setShowNewFeedback}>
                <DialogTrigger asChild>
                  <Button data-testid="new-feedback-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Feedback
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Submit Feedback</DialogTitle>
                    <DialogDescription>
                      Share your feedback, report bugs, or request new features
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="feedback-type">Type</Label>
                      <Select
                        value={newFeedback.feedback_type}
                        onValueChange={(val) => setNewFeedback({ ...newFeedback, feedback_type: val })}
                      >
                        <SelectTrigger data-testid="feedback-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Feedback</SelectItem>
                          <SelectItem value="bug">Bug Report</SelectItem>
                          <SelectItem value="feature_request">Feature Request</SelectItem>
                          <SelectItem value="improvement">Improvement Suggestion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="feedback-title">Title</Label>
                      <Input
                        id="feedback-title"
                        placeholder="Brief summary of your feedback"
                        value={newFeedback.title}
                        onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })}
                        data-testid="feedback-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="feedback-category">Category (Optional)</Label>
                      <Input
                        id="feedback-category"
                        placeholder="e.g., Orders, Listings, Payments"
                        value={newFeedback.category}
                        onChange={(e) => setNewFeedback({ ...newFeedback, category: e.target.value })}
                        data-testid="feedback-category-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="feedback-description">Description</Label>
                      <Textarea
                        id="feedback-description"
                        placeholder="Describe your feedback in detail..."
                        rows={5}
                        value={newFeedback.description}
                        onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })}
                        data-testid="feedback-description-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowNewFeedback(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSubmitFeedback} disabled={submitting} data-testid="submit-feedback-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Submit
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {feedbackLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : myFeedback.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No Feedback Yet</h3>
                  <p className="text-gray-500 mt-1">Share your thoughts to help us improve!</p>
                  <Button className="mt-4" onClick={() => setShowNewFeedback(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myFeedback.map((fb) => {
                  const TypeIcon = feedbackTypeIcons[fb.feedback_type] || HelpCircle;
                  return (
                    <Card key={fb.id} data-testid={`feedback-item-${fb.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <TypeIcon className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{fb.title}</h3>
                              <Badge className={statusColors[fb.status] || 'bg-gray-100'}>
                                {fb.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2">{fb.description}</p>
                            
                            {/* Admin Responses */}
                            {fb.responses && fb.responses.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-medium text-gray-500 mb-2">Admin Response:</p>
                                {fb.responses.map((resp, idx) => (
                                  <div key={resp.id || resp.name || `${resp.title || "resp"}-${idx}`} className="bg-blue-50 rounded-lg p-3 text-sm">
                                    <p className="text-gray-700">{resp.response_text}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      — {resp.admin_name}, {formatDate(resp.created_at)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                              <span>{formatDate(fb.created_at)}</span>
                              {fb.category && (
                                <Badge variant="outline">{fb.category}</Badge>
                              )}
                              {fb.feedback_type === 'feature_request' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUpvote(fb.id); }}
                                  className="flex items-center gap-1 hover:text-blue-600"
                                >
                                  <ThumbsUp className="h-3 w-3" />
                                  {fb.upvotes || 0}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Release Note Detail Modal */}
      <Dialog open={!!selectedNote} onOpenChange={() => setSelectedNote(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedNote && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={releaseTypeColors[selectedNote.note_type] || 'bg-gray-100'}>
                    {selectedNote.note_type?.replace('_', ' ')}
                  </Badge>
                  {selectedNote.version && (
                    <span className="text-sm text-gray-500">v{selectedNote.version}</span>
                  )}
                </div>
                <DialogTitle className="text-xl">{selectedNote.title}</DialogTitle>
                <DialogDescription>
                  Published {formatDate(selectedNote.published_at || selectedNote.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="prose prose-sm max-w-none py-4">
                <div className="whitespace-pre-wrap text-gray-700">
                  {selectedNote.content}
                </div>
              </div>
              {selectedNote.tags && selectedNote.tags.length > 0 && (
                <div className="flex items-center gap-2 pt-4 border-t">
                  {selectedNote.tags.map((tag, idx) => (
                    <Badge key={tag.id || tag.name || `${tag.title || "tag"}-${idx}`} variant="outline">{tag}</Badge>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FeedbackPage;
